/**
 * Geometry Module - Ported from verachem3/misc/numericTools.py
 *
 * Provides 3D geometry calculations for molecular visualization:
 * - Distance between atoms
 * - Angle between three atoms
 * - Dihedral angle between four atoms
 *
 * All functions work with 3D coordinate arrays [x, y, z]
 */

// Type alias for 3D point/vector
export type Vec3 = [number, number, number];

// ============================================================================
// Vector Operations (from numericTools.py)
// ============================================================================

/**
 * Dot product of two vectors
 * Port of: numericTools.py:82-83
 */
export function dot(v1: Vec3, v2: Vec3): number {
  return v1[0] * v2[0] + v1[1] * v2[1] + v1[2] * v2[2];
}

/**
 * Alias for dot product
 */
export const dotProduct = dot;

/**
 * Magnitude (length) of a vector
 * Port of: numericTools.py:88-89
 */
export function magnitude(v: Vec3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

/**
 * Squared magnitude of a vector (avoids sqrt for comparisons)
 * Port of: numericTools.py:91-92
 */
export function magnitudeSquared(v: Vec3): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

/**
 * Normalize a vector to unit length
 * Port of: numericTools.py:94-98
 */
export function normalize(v: Vec3): Vec3 {
  const mag = magnitude(v);
  if (mag === 0) {
    return [0, 0, 0];
  }
  return [v[0] / mag, v[1] / mag, v[2] / mag];
}

/**
 * Cross product of two 3D vectors
 * Port of: numericTools.py:109-114
 */
export function cross(v1: Vec3, v2: Vec3): Vec3 {
  return [
    v1[1] * v2[2] - v1[2] * v2[1],
    v1[2] * v2[0] - v1[0] * v2[2],
    v1[0] * v2[1] - v1[1] * v2[0],
  ];
}

/**
 * Alias for cross product
 */
export const crossProduct = cross;

/**
 * Subtract two vectors: v2 - v1
 */
export function subtract(v1: Vec3, v2: Vec3): Vec3 {
  return [v2[0] - v1[0], v2[1] - v1[1], v2[2] - v1[2]];
}

/**
 * Add two vectors: v1 + v2
 */
export function add(v1: Vec3, v2: Vec3): Vec3 {
  return [v1[0] + v2[0], v1[1] + v2[1], v1[2] + v2[2]];
}

/**
 * Scale a vector by a scalar
 */
export function scale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

// ============================================================================
// Distance Functions
// ============================================================================

/**
 * Calculate distance between two 3D points
 * Port of: numericTools.py:100-102
 *
 * @param p1 - First point [x, y, z]
 * @param p2 - Second point [x, y, z]
 * @returns Distance in same units as input coordinates (typically Angstroms)
 */
export function distance(p1: Vec3, p2: Vec3): number {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const dz = p2[2] - p1[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate squared distance between two 3D points
 * More efficient when only comparing distances (avoids sqrt)
 * Port of: numericTools.py:104-106
 *
 * @param p1 - First point [x, y, z]
 * @param p2 - Second point [x, y, z]
 * @returns Squared distance
 */
export function distanceSquared(p1: Vec3, p2: Vec3): number {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const dz = p2[2] - p1[2];
  return dx * dx + dy * dy + dz * dz;
}

// ============================================================================
// Angle Functions
// ============================================================================

/**
 * Calculate angle formed by three points (angle at p2)
 * Port of: numericTools.py:123-145
 *
 *     p1
 *      \
 *       \  angle
 *        p2 -------- p3
 *
 * @param p1 - First point [x, y, z]
 * @param p2 - Vertex point [x, y, z] (angle is measured here)
 * @param p3 - Third point [x, y, z]
 * @returns Angle in radians (0 to PI)
 */
export function angle(p1: Vec3, p2: Vec3, p3: Vec3): number {
  // Vector from p2 to p1
  const v12: Vec3 = [p1[0] - p2[0], p1[1] - p2[1], p1[2] - p2[2]];
  const r12_2 = magnitudeSquared(v12);

  // Vector from p2 to p3
  const v23: Vec3 = [p3[0] - p2[0], p3[1] - p2[1], p3[2] - p2[2]];
  const r23_2 = magnitudeSquared(v23);

  // Product of magnitudes
  const r13 = Math.sqrt(r12_2 * r23_2);

  // Calculate cosine of angle
  let cosAngle: number;
  if (r13 > 0.0) {
    cosAngle = dot(v12, v23) / r13;
  } else {
    cosAngle = 1.0;
  }

  // Clamp to [-1, 1] to handle floating point errors
  if (cosAngle > 1.0) {
    cosAngle = 1.0;
  }
  if (cosAngle < -1.0) {
    cosAngle = -1.0;
  }

  return Math.acos(cosAngle);
}

/**
 * Calculate angle in degrees
 *
 * @param p1 - First point
 * @param p2 - Vertex point
 * @param p3 - Third point
 * @returns Angle in degrees (0 to 180)
 */
export function angleDegrees(p1: Vec3, p2: Vec3, p3: Vec3): number {
  return radToDeg(angle(p1, p2, p3));
}

// ============================================================================
// Dihedral (Torsion) Angle Functions
// ============================================================================

/**
 * Calculate dihedral (torsion) angle formed by four points
 * Port of: numericTools.py:147-184
 *
 * The dihedral angle is the angle between the plane containing p1-p2-p3
 * and the plane containing p2-p3-p4, measured around the p2-p3 bond.
 *
 *     p1
 *      \
 *       p2 -------- p3
 *                    \
 *                     p4
 *
 * @param p1 - First point [x, y, z]
 * @param p2 - Second point [x, y, z]
 * @param p3 - Third point [x, y, z]
 * @param p4 - Fourth point [x, y, z]
 * @returns Dihedral angle in radians (-PI to PI)
 */
export function dihedral(p1: Vec3, p2: Vec3, p3: Vec3, p4: Vec3): number {
  // Vectors along the bonds
  const v12: Vec3 = [p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]];
  const v32: Vec3 = [p2[0] - p3[0], p2[1] - p3[1], p2[2] - p3[2]];
  const v34: Vec3 = [p4[0] - p3[0], p4[1] - p3[1], p4[2] - p3[2]];

  // Normal vectors to the two planes
  const cross1 = cross(v12, v32);
  const cross2 = cross(v32, v34);

  // Magnitudes of the normal vectors
  const mcross1 = magnitude(cross1);
  const mcross2 = magnitude(cross2);

  // Reciprocal magnitudes (with zero check)
  const rMagCross1 = mcross1 > 0 ? 1.0 / mcross1 : 0.0;
  const rMagCross2 = mcross2 > 0 ? 1.0 / mcross2 : 0.0;

  // Dot product of the normal vectors
  const dotCross12 = dot(cross1, cross2);

  // Cross product of the normal vectors
  const crossCross12 = cross(cross1, cross2);

  // Determine sign of dihedral
  const dotCrossCross = dot(v32, crossCross12);

  // Calculate cosine of dihedral angle
  let cosDihedral = dotCross12 * rMagCross1 * rMagCross2;

  // Clamp to [-1, 1] to handle floating point errors
  if (cosDihedral > 1.0) {
    cosDihedral = 1.0;
  }
  if (cosDihedral < -1.0) {
    cosDihedral = -1.0;
  }

  let dihedralAngle = Math.acos(cosDihedral);

  // Apply sign based on direction
  if (dotCrossCross > 0.0) {
    dihedralAngle = -dihedralAngle;
  }

  return dihedralAngle;
}

/**
 * Calculate dihedral angle in degrees
 *
 * @param p1 - First point
 * @param p2 - Second point
 * @param p3 - Third point
 * @param p4 - Fourth point
 * @returns Dihedral angle in degrees (-180 to 180)
 */
export function dihedralDegrees(p1: Vec3, p2: Vec3, p3: Vec3, p4: Vec3): number {
  return radToDeg(dihedral(p1, p2, p3, p4));
}

// ============================================================================
// Unit Conversion Utilities
// ============================================================================

/**
 * Convert radians to degrees
 */
export function radToDeg(rad: number): number {
  return rad * (180.0 / Math.PI);
}

/**
 * Convert degrees to radians
 */
export function degToRad(deg: number): number {
  return deg * (Math.PI / 180.0);
}

// ============================================================================
// Measurement Result Types
// ============================================================================

/**
 * Result of a distance measurement
 */
export interface DistanceMeasurement {
  type: 'distance';
  atom1: number;
  atom2: number;
  value: number; // in Angstroms
  label: string; // formatted for display
}

/**
 * Result of an angle measurement
 */
export interface AngleMeasurement {
  type: 'angle';
  atom1: number;
  atom2: number; // vertex
  atom3: number;
  value: number; // in degrees
  label: string;
}

/**
 * Result of a dihedral measurement
 */
export interface DihedralMeasurement {
  type: 'dihedral';
  atom1: number;
  atom2: number;
  atom3: number;
  atom4: number;
  value: number; // in degrees
  label: string;
}

/**
 * Union type for all measurement types
 */
export type Measurement = DistanceMeasurement | AngleMeasurement | DihedralMeasurement;

// ============================================================================
// Measurement Helper Functions
// ============================================================================

/**
 * Create a distance measurement result
 */
export function createDistanceMeasurement(
  atom1: number,
  atom2: number,
  p1: Vec3,
  p2: Vec3
): DistanceMeasurement {
  const dist = distance(p1, p2);
  return {
    type: 'distance',
    atom1,
    atom2,
    value: dist,
    label: `${dist.toFixed(2)} \u00C5`, // Angstrom symbol
  };
}

/**
 * Create an angle measurement result
 */
export function createAngleMeasurement(
  atom1: number,
  atom2: number,
  atom3: number,
  p1: Vec3,
  p2: Vec3,
  p3: Vec3
): AngleMeasurement {
  const ang = angleDegrees(p1, p2, p3);
  return {
    type: 'angle',
    atom1,
    atom2,
    atom3,
    value: ang,
    label: `${ang.toFixed(1)}\u00B0`, // Degree symbol
  };
}

/**
 * Create a dihedral measurement result
 */
export function createDihedralMeasurement(
  atom1: number,
  atom2: number,
  atom3: number,
  atom4: number,
  p1: Vec3,
  p2: Vec3,
  p3: Vec3,
  p4: Vec3
): DihedralMeasurement {
  const dih = dihedralDegrees(p1, p2, p3, p4);
  return {
    type: 'dihedral',
    atom1,
    atom2,
    atom3,
    atom4,
    value: dih,
    label: `${dih.toFixed(1)}\u00B0`,
  };
}

// ============================================================================
// Additional Geometry Utilities
// ============================================================================

/**
 * Calculate the midpoint between two points
 */
export function midpoint(p1: Vec3, p2: Vec3): Vec3 {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, (p1[2] + p2[2]) / 2];
}

/**
 * Calculate the centroid of an array of points
 */
export function centroid(points: Vec3[]): Vec3 {
  if (points.length === 0) {
    return [0, 0, 0];
  }

  let sumX = 0,
    sumY = 0,
    sumZ = 0;
  for (const p of points) {
    sumX += p[0];
    sumY += p[1];
    sumZ += p[2];
  }

  const n = points.length;
  return [sumX / n, sumY / n, sumZ / n];
}

/**
 * Calculate bounding box of points
 */
export function boundingBox(points: Vec3[]): { min: Vec3; max: Vec3 } {
  if (points.length === 0) {
    return {
      min: [0, 0, 0],
      max: [0, 0, 0],
    };
  }

  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;

  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[2] < minZ) minZ = p[2];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] > maxY) maxY = p[1];
    if (p[2] > maxZ) maxZ = p[2];
  }

  return {
    min: [minX, minY, minZ],
    max: [maxX, maxY, maxZ],
  };
}

/**
 * Check if a point is within a given distance of another point
 */
export function isWithinDistance(p1: Vec3, p2: Vec3, maxDistance: number): boolean {
  return distanceSquared(p1, p2) <= maxDistance * maxDistance;
}
